/**
 * Spand-fragtgebyr — cart-side logik (plain theme, ingen Shopify Function).
 *
 * REGLEN: merchandise-subtotal (EKSKL. gebyr-linjerne) >= tærskel OG spand i kurv
 * → læg gebyret ("Fragt – stor pakke") på som varelinje(r). Under tærskel:
 * fjern gebyret igen (spande sejler med på normal Shipmondo-fragt).
 *
 * SUBTOTAL-FORMLEN er byte-identisk med dashboardets lib/spand-fee.js:
 *   merchandiseSubtotal = Σ over IKKE-gebyr-linjer af linjepris EFTER line-rabat.
 * /cart.js's final_line_price (i ØRE) ER linjeprisen efter line-rabat → /100.
 *
 * Config kommer fra window.dpSpandFeeConfig (injiceret fra shop-metafield i
 * theme.liquid): { active, threshold, fee, mode, label, feeVariantId,
 * spandProductIds:[...] }.
 *
 * DEGRESSIVT GEBYR (65 kr for første stor pakke, 35 kr for hver ekstra):
 * kan ikke laves med antal × én pris, så det kræver TO gebyr-varianter.
 * Config får derfor to felter mere: feeExtra + feeVariantIdExtra.
 *
 *   Mangler feeVariantIdExtra (eller er den tom) → nøjagtig samme adfærd som
 *   før, ad samme kodesti: ÉN gebyr-linje med `desired` stk.
 *
 *   Det degressive gebyr kræver BEGGE dele: feeVariantIdExtra OG
 *   mode: per_spand_qty_degressiv. Kun den ene halvdel giver den gamle pris.
 *   Samme krav håndhæves fra dashboard-siden, så fase D ikke kan
 *   halvvejs-aktivere sig selv, og et tilbagerul af enten mode eller variant
 *   bringer prisen tilbage — uden deploy.
 *
 * Re-evalueres på hver kurv-ændring (cart:refresh / product:added) + ved load.
 * Loop-guard via `busy`.
 */
(function () {
  'use strict';

  var FEE_PROPERTY = '_auto_fee';
  var FEE_PROPERTY_VALUE = 'spand';
  var MODES = {
    PER_CART: 'per_cart',
    PER_DISTINCT: 'per_distinct_spand',
    PER_QTY: 'per_spand_qty',
    PER_QTY_DEG: 'per_spand_qty_degressiv',
  };

  function cfg() { return window.dpSpandFeeConfig || null; }
  function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

  // Ekstra-gebyrvariantens id, normaliseret. '' = feltet er ikke sat → gammel
  // adfærd. Tomme strenge, 0, null og "null" tæller alle som ikke sat, så et
  // halvt udfyldt metafelt ikke kan slå den nye sti til ved et uheld.
  function extraVariantId(c) {
    if (!c) return '';
    var v = c.feeVariantIdExtra;
    if (v === null || v === undefined) return '';
    v = String(v).trim();
    if (v === '' || v === '0' || v === 'null' || v === 'undefined') return '';
    return v;
  }

  // Er denne /cart.js-item en gebyr-linje? Match på ÉN AF BEGGE fee-varianter
  // ELLER _auto_fee-prop.
  // KRITISK: misser den ekstra-varianten, tæller den linje med i
  // merchandiseSubtotal og skubber kurven over tærsklen af sig selv.
  function isFeeItem(it, c) {
    if (c) {
      if (c.feeVariantId && String(it.id) === String(c.feeVariantId)) return true;
      var ex = extraVariantId(c);
      if (ex && String(it.id) === ex) return true;
    }
    var props = it.properties || {};
    return !!(props && Object.prototype.hasOwnProperty.call(props, FEE_PROPERTY));
  }

  // merchandise-subtotal = Σ final_line_price/100 over ikke-gebyr-linjer (DEN delte formel).
  function merchandiseSubtotal(items, c) {
    var sum = 0;
    for (var i = 0; i < items.length; i++) {
      if (isFeeItem(items[i], c)) continue;
      sum += (Number(items[i].final_line_price) || 0) / 100;
    }
    return round2(sum);
  }

  function spandItems(items, c) {
    var set = {};
    (c.spandProductIds || []).forEach(function (id) { set[String(id)] = 1; });
    return items.filter(function (it) {
      return !isFeeItem(it, c) && set[String(it.product_id)] === 1;
    });
  }

  // 0 hvis intet gebyr skal ligge, ellers antal gebyr-enheder ud fra mode.
  function desiredFeeUnits(items, c) {
    var sl = spandItems(items, c);
    if (sl.length === 0) return 0;
    if (merchandiseSubtotal(items, c) < (Number(c.threshold) || 0)) return 0;
    if (c.mode === MODES.PER_QTY || c.mode === MODES.PER_QTY_DEG) {
      // Samme ANTAL enheder som per_spand_qty. Det er fordelingen over de to
      // varianter der gør den degressiv, ikke antallet.
      return sl.reduce(function (s, it) { return s + (Number(it.quantity) || 0); }, 0);
    }
    if (c.mode === MODES.PER_DISTINCT) {
      var ids = {};
      sl.forEach(function (it) { ids[String(it.product_id)] = 1; });
      return Object.keys(ids).length;
    }
    return 1; // PER_CART
  }

  // Del N enheder op i 1 første + (N-1) ekstra. Samme funktion som dashboardets
  // feeUnitsSplit(); ændres den ét sted, skal den ændres begge steder.
  function feeUnitsSplit(units) {
    var n = Number(units) || 0;
    return { foerste: n > 0 ? 1 : 0, ekstra: n > 1 ? n - 1 : 0 };
  }

  function findFeeLine(cart, c) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (isFeeItem(items[i], c)) return items[i];
    }
    return null;
  }

  function findFeeLineByVariant(cart, variantId) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].id) === String(variantId)) return items[i];
    }
    return null;
  }

  function cartAjax(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      // Fetch rejecter ikke på 4xx. Et fejlet add/change (fx 422 udsolgt gebyr-variant)
      // må IKKE behandles som success → kast, så evaluateAndApply's .catch nulstiller
      // busy UDEN at dispatche cart:refresh (retry ved næste kurv-ændring).
      return r.json().then(function (j) {
        if (!r.ok || (j && (j.status >= 400 || j.description))) {
          var err = new Error('cart-ajax ' + ((j && j.description) ? j.description : r.status));
          err.cartError = j;
          throw err;
        }
        return j;
      });
    });
  }

  // Bringer ÉN gebyr-variant til `want` stk. Resolver true hvis kurven blev
  // ændret, false hvis den allerede stod rigtigt (så vi ikke dispatcher
  // cart:refresh uden grund).
  //
  // `cart` er snapshottet fra før operationen. Det er sikkert at genbruge på
  // tværs af de to kald: en line-key er variant + properties-hash, og den
  // ændres ikke af at en ANDEN linjes mængde bliver sat.
  function setFeeLine(cart, variantId, want) {
    var line = findFeeLineByVariant(cart, variantId);
    var have = line ? (Number(line.quantity) || 0) : 0;
    if (have === want) return Promise.resolve(false);
    if (!line) {
      var props = {}; props[FEE_PROPERTY] = FEE_PROPERTY_VALUE;
      return cartAjax('/cart/add.js', {
        items: [{ id: Number(variantId), quantity: want, properties: props }],
      }).then(function () { return true; });
    }
    // findes allerede → sæt ny mængde (0 = fjern). Brug line-key (robust).
    return cartAjax('/cart/change.js', { id: line.key, quantity: want })
      .then(function () { return true; });
  }

  var busy = false;

  function evaluateAndApply() {
    var c = cfg();
    if (busy || !c || !c.active) return;
    if (!c.feeVariantId) { return; } // intet gebyr-produkt sat endnu
    // busy holder om HELE operationen (begge kald), ikke om ét kald. Ellers når
    // cart:refresh at fyre midt imellem de to linjer, og vores egen handler
    // kører oveni sig selv.
    busy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var desired = desiredFeeUnits(items, c);
        var ekstraId = extraVariantId(c);

        if (!ekstraId) {
          // ===== GAMMEL STI: én gebyr-linje. Uændret, byte for byte. =====
          var feeLine = findFeeLine(cart, c);
          var current = feeLine ? (Number(feeLine.quantity) || 0) : 0;
          if (desired === current) return null; // ingen ændring
          if (current === 0) {
            var props = {}; props[FEE_PROPERTY] = FEE_PROPERTY_VALUE;
            return cartAjax('/cart/add.js', { items: [{ id: Number(c.feeVariantId), quantity: desired, properties: props }] });
          }
          // findes allerede → sæt ny mængde (0 = fjern). Brug line-key (robust).
          return cartAjax('/cart/change.js', { id: feeLine.key, quantity: desired });
        }

        // ===== FELTET ER SAT: to linjer holdes i synk. =====
        // Fordelingen afhaenger af mode, saa kontakten kraever BEGGE dele. Er
        // varianten sat mens mode stadig er den gamle, faar kunden praecis den
        // gamle pris (N x foerste, nul ekstra) - og en ekstra-linje der maatte
        // ligge tilbage fra en degressiv periode bliver ryddet vaek af sig selv,
        // fordi den oenskede maengde er 0. Det er det samme krav som dashboardets
        // spaerring, bare fra den anden side: ingen af de to halvdele kan
        // aktivere gebyret alene.
        var split = (c.mode === MODES.PER_QTY_DEG)
          ? feeUnitsSplit(desired)
          : { foerste: desired, ekstra: 0 };
        var foersteId = String(c.feeVariantId);
        var foersteLine = findFeeLineByVariant(cart, foersteId);
        var ekstraLine = findFeeLineByVariant(cart, ekstraId);
        var haveFoerste = foersteLine ? (Number(foersteLine.quantity) || 0) : 0;
        var haveEkstra = ekstraLine ? (Number(ekstraLine.quantity) || 0) : 0;
        if (haveFoerste === split.foerste && haveEkstra === split.ekstra) return null;

        // RÆKKEFØLGE: kurven må aldrig stå med en ekstra-linje uden en
        // første-linje. Skal ekstra-linjen OP, sætter vi første-linjen først.
        // Ellers (ekstra ned eller uændret) tager vi ekstra-linjen først.
        //   0 → 2 spande: ekstra op  → første først (aldrig ekstra alene)
        //   2 → 0 spande: ekstra ned → ekstra først (aldrig ekstra alene)
        //   ved skiftet i fase D (første=2, ekstra=0 → 1 + 1): ekstra op →
        //   første ned først, så mellemtilstanden er 65 kr og ikke 130 kr.
        // Den samme rækkefølge gør at hvis kald nr. 2 fejler, er det ALTID
        // første-linjen der bliver stående alene — aldrig en forældreløs ekstra.
        var trin = (split.ekstra > haveEkstra)
          ? [[foersteId, split.foerste], [ekstraId, split.ekstra]]
          : [[ekstraId, split.ekstra], [foersteId, split.foerste]];

        // Sekventielt: fejler det FØRSTE kald, køres det andet slet ikke, og
        // kurven er urørt. Fejler det ANDET, nulstiller .catch busy uden at
        // dispatche cart:refresh → vi prøver igen ved næste kurv-ændring.
        var aendret = false;
        return setFeeLine(cart, trin[0][0], trin[0][1])
          .then(function (didA) {
            aendret = aendret || didA;
            return setFeeLine(cart, trin[1][0], trin[1][1]);
          })
          .then(function (didB) {
            aendret = aendret || didB;
            return aendret ? true : null;
          });
      })
      .then(function (res) {
        if (res) {
          // bed temaet re-rendre kurv/drawer (busy stadig true → vores handler skipper)
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        busy = false;
      })
      .catch(function () { busy = false; });
  }

  // Subscribe på begge kurv-mutations-stier i Prestige + ved load.
  function init() {
    document.documentElement.addEventListener('cart:refresh', evaluateAndApply);
    document.addEventListener('product:added', evaluateAndApply);
    // KRITISK: Prestiges in-cart qty-edit/remove re-rendrer via _rerenderCart UDEN at
    // dispatche cart:refresh, men dispatcher 'theme:loading:end' på document efter
    // /cart/change.js. Uden denne ville en mængde-ændring i kurven efterlade et forældet
    // gebyr (over-/underopkrævning). Debounce så de bredere theme:loading:end-brug
    // (PDP variant-loads) ikke spammer /cart.js — evaluateAndApply er idempotent + busy-guarded.
    var t = null;
    document.addEventListener('theme:loading:end', function () {
      if (t) clearTimeout(t);
      t = setTimeout(evaluateAndApply, 150);
    });
    evaluateAndApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
